export type MemberStage = 'rising' | 'steady' | 'at_risk' | 'silent' | 'departed';

// Engagement/momentum stages — single source of truth for their order, labels,
// colours, and one-line meaning. Shared by the StageBadge, the members-table
// filter, and the Overview momentum cards so they can never drift.
//
// ONE AXIS, ONE JOB: a stage says how someone's activity is trending, and that is
// only a question about a member who is still here. "Departed" is not a momentum
// bucket and is deliberately absent from this file's display taxonomy — departure
// is carried by the CONTRIBUTOR ROLE axis instead (`TERMINAL_ROLE` in quadrant.ts),
// which is terminal, overrides whatever the activity numbers say, and already sits
// beside the stage on every surface that shows one: ContributorBadge on the member
// profile, RoleJourney in the members table and the hover card, and the hover
// card's own "Role" tile. Showing it on both axes said the same thing twice, and on
// the momentum surfaces (cards, donut, scatter filter) it drew a bucket the API
// never fills — `/contributors` censuses stages over current members only.
export type Stage = 'rising' | 'steady' | 'at_risk' | 'silent';

// Momentum order, best → quietest. The whole taxonomy: every stage here is a live
// stage, so any surface may iterate this without a terminal-value special case.
export const STAGE_ORDER: Stage[] = ['rising', 'steady', 'at_risk', 'silent'];

// The value `member_stats.stage` carries for someone who has left (the loader's
// DEPARTED_STAGE). It is a STORAGE marker, not a display stage: it exists so ex-
// members can't fall into `silent` and be counted as people ignoring you, and it is
// never rendered on the momentum axis — `stageMeta()` returns null for it and every
// badge skips it. It's kept out of `Stage` so nothing can enumerate it by accident.
export const TERMINAL_STAGE = 'departed';

export interface StageMeta {
  label: string;
  color: string;
  blurb: string;
}

// Colours: a semantic set tuned to sit in the light theme — deep green for
// growth, the theme's slate-blue for a steady baseline, a deep red for danger,
// and a solid slate for silence. All high-contrast on white.
export const STAGE_META: Record<Stage, StageMeta> = {
  rising: { label: 'Rising', color: '#157a49', blurb: 'gaining momentum (last 14 days vs the 14 before)' },
  steady: { label: 'Steady', color: '#4a6aa8', blurb: 'showed up recently, holding roughly level' },
  at_risk: { label: 'At risk', color: '#bd3a30', blurb: 'a valuable contributor whose activity dropped steeply' },
  silent: { label: 'Silent', color: '#6b7280', blurb: 'no activity at all in the last 14 days' },
};

/** How to render a stage off the wire — null means "don't", which is the terminal
 *  storage marker's answer and the one rule keeping "Departed" off this axis. An
 *  unrecognised value still falls back to `silent` rather than vanishing, so a new
 *  server-side stage shows up as a badge instead of a hole. */
export function stageMeta(stage: string | null | undefined): StageMeta | null {
  if (stage == null || stage === TERMINAL_STAGE) return null;
  return STAGE_META[stage as Stage] ?? STAGE_META.silent;
}
